import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SHIPPING_METHOD_CODES } from '../shipping-methods.const';
import { Address } from '../../indi-customer';
import { AddressToWrite } from '../../APIVerkoopordersToWrite';
import { User } from '../../user';
import { AddressForm, ShippingDetailsForm } from '../../form-controls';

@Component({
  selector: 'app-new-salesorder-shipping-details',
  templateUrl: './new-salesorder-shipping-details.component.html',
  styleUrls: ['./new-salesorder-shipping-details.component.scss'],
})
export class NewSalesorderShippingDetailsComponent implements AfterViewInit {
  static requiredValidator = Validators.required.bind(null);
  static readonly COUNTRY_PATTERN: RegExp = /^(NL|DE)$/;

  @Input() shippingDetails!: FormGroup<ShippingDetailsForm>;
  @Input() deliveryAddress?: Address;
  @Input() addresses: Address[] = [];
  @Input() users: User[] = [];
  @Input() nightShippingAddress!: Address;
  @Input() hasNightShipping: boolean = false;
  @Input() hasPickUpAtCounter: boolean = false;
  @Output() invoiceEmailChanged = new EventEmitter<string>();
  @Output() preferredUserUUIDEmitter = new EventEmitter<string>();

  @ViewChild('preferredUserCheckbox') preferredUserCheckbox!: ElementRef<HTMLInputElement>;

  userUUIDPreference: string | undefined;

  readonly AVAILABLE_SHIPPING_METHODS: string[] = Object.keys(SHIPPING_METHOD_CODES);

  get valid(): boolean {
    return this.shippingDetails.valid;
  }

  static createShippingDetails(
    fb: FormBuilder,
    deliveryAddress: Address,
    userUUIDPreference: string
  ): FormGroup<ShippingDetailsForm> {
    return fb.group<ShippingDetailsForm>({
      shippingMethod: new FormControl<string>('', {
        nonNullable: true,
        validators: [this.requiredValidator],
      }),
      userUUID: new FormControl<string>(userUUIDPreference, {
        nonNullable: true,
        validators: [this.requiredValidator],
      }),
      deliveryAddress: fb.group<AddressForm>({
        companyName: new FormControl<string>(deliveryAddress.companyName, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        firstName: new FormControl<string>(deliveryAddress.firstName, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        lastName: new FormControl<string>(deliveryAddress.lastName, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        address: new FormControl<string>(deliveryAddress.address, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        postalCode: new FormControl<string>(deliveryAddress.postalCode, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        city: new FormControl<string>(deliveryAddress.city, {
          nonNullable: true,
          validators: [this.requiredValidator],
        }),
        countryCode: new FormControl<string>(deliveryAddress.countryCode, {
          nonNullable: true,
          validators: [this.requiredValidator, Validators.pattern(this.COUNTRY_PATTERN)],
        }),
        email: new FormControl<string>('', {
          nonNullable: true,
        }),
      }),
    });
  }

  ngAfterViewInit(): void {
    const uuid: string | undefined = this.shippingDetails.get('userUUID')?.value;
    if (uuid) {
      this.userUUIDPreference = uuid;
      this.preferredUserCheckbox.nativeElement.checked = true;
      this.setSelectedUser(uuid);
    }
  }

  initializeAddressData(): void {
    if (this.hasNightShipping) {
      this.shippingDetails.patchValue({ shippingMethod: 'Nachtbezorging' });
    } else if (this.hasPickUpAtCounter) {
      this.shippingDetails.patchValue({ shippingMethod: 'Afhalen bij de balie' });
    } else {
      this.shippingDetails.patchValue({ shippingMethod: 'Dagbezorging' });
    }
    this.shippingDetails.get('shippingMethod')?.valueChanges.subscribe(() => {
      this.setDeliveryAddressFormValues(0);
    });
    this.setDeliveryAddressFormValues(0);
  }

  setDeliveryAddressFormValues(index: Event | number): void {
    const addressGroup = this.shippingDetails.get('deliveryAddress') as FormGroup;
    const shippingMethod = this.shippingDetails.get('shippingMethod')?.value;
    const defaultShippingAddress = this.addresses[0];
    switch (shippingMethod) {
      case 'Dagbezorging':
        if (index instanceof Event) {
          const select = index.target as HTMLSelectElement;
          index = +select.value;
        }
        this.setDeliveryAddressValues(this.addresses[index]);
        break;
      case 'Nachtbezorging':
        this.setDeliveryAddressValues(this.nightShippingAddress);
        break;
      case 'Ander adres kiezen':
        this.setDeliveryAddressValues(this.deliveryAddress ?? this.addresses[0]);
        break;
      case 'Afhalen bij de balie':
        this.setDeliveryAddressValues(defaultShippingAddress);
        break;
      default:
        break;
    }
    addressGroup.updateValueAndValidity({ emitEvent: false });
  }

  setDeliveryAddressValues(deliveryAddress: Address): void {
    const deliveryAddressControls = this.shippingDetails.controls.deliveryAddress.controls;

    deliveryAddressControls.companyName.setValue(deliveryAddress.companyName);
    deliveryAddressControls.firstName.setValue(deliveryAddress.firstName);
    deliveryAddressControls.lastName.setValue(deliveryAddress.lastName);
    deliveryAddressControls.address.setValue(deliveryAddress.address);
    deliveryAddressControls.postalCode.setValue(deliveryAddress.postalCode);
    deliveryAddressControls.city.setValue(deliveryAddress.city);
    deliveryAddressControls.countryCode.setValue(deliveryAddress.countryCode);
  }

  onUserSelect(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedUserUUID: string = target.value;

    if (!this.setSelectedUser(selectedUserUUID)) {
      return;
    }

    if (selectedUserUUID === this.userUUIDPreference) {
      this.preferredUserCheckbox.nativeElement.checked = true;
      return;
    }
    this.preferredUserCheckbox.nativeElement.checked = false;
  }

  setSelectedUser(selectedUserUUID: string): boolean {
    const fullUser: User | undefined = this.users.find((user: User) => user.uuid === selectedUserUUID);
    if (!fullUser) {
      console.warn(`No user found with UUID ${selectedUserUUID}`);
      return false;
    }
    this.invoiceEmailChanged.emit(
      fullUser.invoicesEmailAddress ? fullUser.invoicesEmailAddress : fullUser.loginEmailAddress
    );

    this.shippingDetails.controls.deliveryAddress.controls.email.setValue(fullUser.loginEmailAddress);
    return true;
  }

  onPreferredUserToggle(): void {
    this.userUUIDPreference = this.preferredUserCheckbox.nativeElement.checked
      ? (this.shippingDetails.get('userUUID')?.value ?? '')
      : '';
    this.preferredUserUUIDEmitter.emit(this.userUUIDPreference);
  }

  getDeliveryAddress(): AddressToWrite {
    return this.shippingDetails.controls.deliveryAddress.value as AddressToWrite;
  }

  getShippingMethod(): string {
    return this.shippingDetails.controls.shippingMethod.value;
  }

  getUserUUID(): string {
    return this.shippingDetails.controls.userUUID.value;
  }
}
